import logger from '../utils/logger';
/**
 * Push Notification Service for Chess Kingdom
 * Handles both browser Web Push notifications and in-app toasts
 */

class NotificationService {
  constructor() {
    this.permission = 'default';
    this.soundEnabled = true;
    this.sounds = {};
    this.initialized = false;
  }

  /**
   * Initialize the notification service
   */
  async init() {
    if (this.initialized) return;
    
    // Check if browser supports notifications
    if ('Notification' in window) {
      this.permission = Notification.permission;
    }
    
    // Preload notification sounds
    this.loadSounds();
    
    // Load user preferences from localStorage
    this.loadPreferences();
    
    this.initialized = true;
  }

  /**
   * Load notification sounds
   */
  loadSounds() {
    // Using Web Audio API for better control
    this.audioContext = null;
    
    // Sound configurations (we'll generate tones programmatically)
    this.soundConfigs = {
      yourTurn: { frequency: 523.25, duration: 0.15, type: 'sine', repeat: 2 }, // C5 note, pleasant chime
      matchFound: { frequency: 659.25, duration: 0.2, type: 'sine', repeat: 3 }, // E5 note, exciting
      message: { frequency: 440, duration: 0.1, type: 'sine', repeat: 1 }, // A4 note, subtle
      notification: { frequency: 587.33, duration: 0.12, type: 'sine', repeat: 1 }, // D5 note
      gameInvite: { frequency: 783.99, duration: 0.15, type: 'sine', repeat: 2 }, // G5 note
    };
  }

  /**
   * Play a notification sound
   */
  playSound(soundType = 'notification') {
    if (!this.soundEnabled) return;
    
    try {
      // Initialize AudioContext on first user interaction
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const config = this.soundConfigs[soundType] || this.soundConfigs.notification;
      
      for (let i = 0; i < config.repeat; i++) {
        setTimeout(() => {
          this.playTone(config.frequency, config.duration, config.type);
        }, i * (config.duration * 1000 + 50));
      }
    } catch (error) {
      logger.error('Failed to play notification sound:', error);
    }
  }

  /**
   * Play a single tone
   */
  playTone(frequency, duration, type = 'sine') {
    if (!this.audioContext) return;
    
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    oscillator.frequency.value = frequency;
    oscillator.type = type;
    
    // Fade in and out for smoother sound
    const now = this.audioContext.currentTime;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01);
    gainNode.gain.linearRampToValueAtTime(0, now + duration);
    
    oscillator.start(now);
    oscillator.stop(now + duration);
  }

  /**
   * Load user preferences from localStorage
   */
  loadPreferences() {
    try {
      const prefs = localStorage.getItem('chessKingdomNotificationPrefs');
      if (prefs) {
        const parsed = JSON.parse(prefs);
        this.soundEnabled = parsed.soundEnabled !== false;
      }
      
      // Load per-event preferences
      const eventPrefs = localStorage.getItem('notificationPreferences');
      if (eventPrefs) {
        this.eventPreferences = JSON.parse(eventPrefs);
      } else {
        // Default preferences - all enabled
        this.eventPreferences = {
          yourTurn: true,
          matchFound: true,
          friendRequests: true,
          messages: true,
          spectatorRequests: true,
          tournaments: true,
        };
      }
    } catch (e) {
      this.eventPreferences = {
        yourTurn: true,
        matchFound: true,
        friendRequests: true,
        messages: true,
        spectatorRequests: true,
        tournaments: true,
      };
    }
  }

  /**
   * Check if a specific event type should send notifications
   */
  isEventEnabled(eventType) {
    // Reload preferences in case they changed
    const eventPrefs = localStorage.getItem('notificationPreferences');
    if (eventPrefs) {
      try {
        this.eventPreferences = JSON.parse(eventPrefs);
      } catch (e) {
        logger.warn('Failed to parse notification preferences:', e);
      }
    }
    
    // If the event type exists in preferences, use it; otherwise default to true
    return this.eventPreferences?.[eventType] !== false;
  }

  /**
   * Save user preferences to localStorage
   */
  savePreferences() {
    try {
      localStorage.setItem('chessKingdomNotificationPrefs', JSON.stringify({
        soundEnabled: this.soundEnabled,
        permission: this.permission
      }));
    } catch (e) {
      logger.error('Failed to save notification preferences:', e);
    }
  }

  /**
   * Request permission for browser notifications
   */
  async requestPermission() {
    if (!('Notification' in window)) {
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      this.permission = 'granted';
      return 'granted';
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      this.savePreferences();
      return permission;
    }

    return Notification.permission;
  }

  /**
   * Check if notifications are enabled
   */
  isEnabled() {
    return this.permission === 'granted';
  }

  /**
   * Send a browser push notification
   */
  async sendPushNotification(title, options = {}) {
    // Always try to play sound for important notifications
    if (options.sound) {
      this.playSound(options.sound);
    }

    // Check if we have permission for browser notifications
    if (!this.isEnabled()) {
      return null;
    }

    // Don't show browser notification if tab is focused
    if (document.hasFocus() && !options.forceShow) {
      return null;
    }

    try {
      const notification = new Notification(title, {
        icon: options.icon || '/favicon.ico',
        badge: '/favicon.ico',
        body: options.body || '',
        tag: options.tag || 'chess-kingdom',
        renotify: options.renotify || false,
        silent: true, // We handle our own sounds
        requireInteraction: options.requireInteraction || false,
        data: options.data || {}
      });

      // Handle click
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        if (options.onClick) {
          options.onClick(event);
        }
        notification.close();
      };

      // Auto-close after timeout
      if (options.timeout) {
        setTimeout(() => notification.close(), options.timeout);
      }

      return notification;
    } catch (error) {
      logger.error('Failed to show notification:', error);
      return null;
    }
  }

  /**
   * Notify: Your Turn
   */
  notifyYourTurn(opponentName = 'Opponent') {
    // Your Turn is always enabled (marked as required in settings)
    return this.sendPushNotification('♟️ Your Turn!', {
      body: `${opponentName} made a move. It's your turn to play!`,
      tag: 'your-turn',
      renotify: true,
      sound: 'yourTurn',
      requireInteraction: true,
      timeout: 10000
    });
  }

  /**
   * Notify: Match Found
   */
  notifyMatchFound(roomName = 'Quick Match') {
    // Check if matchFound (game invites) notifications are enabled
    if (!this.isEventEnabled('matchFound')) {
      return null;
    }
    return this.sendPushNotification('🎮 Match Found!', {
      body: `Your game "${roomName}" is ready to start!`,
      tag: 'match-found',
      renotify: true,
      sound: 'matchFound',
      requireInteraction: true,
      forceShow: true
    });
  }

  /**
   * Notify: Game Invitation
   */
  notifyGameInvite(fromUser, roomName) {
    // Check if matchFound (game invites) notifications are enabled
    if (!this.isEventEnabled('matchFound')) {
      return null;
    }
    return this.sendPushNotification('🎯 Game Invitation', {
      body: `${fromUser} invited you to play "${roomName}"`,
      tag: 'game-invite',
      renotify: true,
      sound: 'gameInvite',
      requireInteraction: true
    });
  }

  /**
   * Notify: Friend Request
   */
  notifyFriendRequest(fromUser) {
    // Check if friendRequests notifications are enabled
    if (!this.isEventEnabled('friendRequests')) {
      return null;
    }
    return this.sendPushNotification('👋 Friend Request', {
      body: `${fromUser} wants to be your friend!`,
      tag: 'friend-request',
      sound: 'notification',
      timeout: 8000
    });
  }

  /**
   * Notify: Spectator Request
   */
  notifySpectatorRequest(userName) {
    // Check if spectatorRequests notifications are enabled
    if (!this.isEventEnabled('spectatorRequests')) {
      return null;
    }
    return this.sendPushNotification('👁️ Spectator Request', {
      body: `${userName} wants to watch your game`,
      tag: 'spectator-request',
      sound: 'notification',
      timeout: 8000
    });
  }

  /**
   * Notify: Rejoin Request
   */
  notifyRejoinRequest(userName, roomName) {
    // Rejoin requests use same preference as game invites (matchFound)
    if (!this.isEventEnabled('matchFound')) {
      return null;
    }
    return this.sendPushNotification('🔄 Rejoin Request', {
      body: `${userName} wants to rejoin "${roomName}"`,
      tag: 'rejoin-request',
      sound: 'notification',
      timeout: 8000
    });
  }

  /**
   * Notify: Message Received
   */
  notifyMessage(fromUser, preview) {
    // Check if messages notifications are enabled
    if (!this.isEventEnabled('messages')) {
      return null;
    }
    return this.sendPushNotification(`💬 ${fromUser}`, {
      body: preview.substring(0, 100),
      tag: 'message-' + fromUser,
      sound: 'message',
      timeout: 5000
    });
  }

  /**
   * Notify: Friend Online
   */
  notifyFriendOnline(friendName) {
    // Friend online notifications use the friendRequests preference
    if (!this.isEventEnabled('friendRequests')) {
      return null;
    }
    return this.sendPushNotification('🟢 Friend Online', {
      body: `${friendName} is now online`,
      tag: 'friend-online-' + friendName,
      sound: 'notification',
      timeout: 5000
    });
  }

  /**
   * Notify: Tournament Update
   */
  notifyTournament(title, body) {
    // Check if tournaments notifications are enabled
    if (!this.isEventEnabled('tournaments')) {
      return null;
    }
    return this.sendPushNotification(`🏆 ${title}`, {
      body: body,
      tag: 'tournament',
      sound: 'notification',
      timeout: 10000
    });
  }

  /**
   * Notify: Game Over
   */
  notifyGameOver(result, reason) {
    const isWin = result === 'win';
    return this.sendPushNotification(
      isWin ? '🏆 Victory!' : '💔 Game Over',
      {
        body: isWin ? `You won by ${reason}!` : `You lost by ${reason}`,
        tag: 'game-over',
        sound: isWin ? 'matchFound' : 'notification',
        timeout: 10000
      }
    );
  }

  /**
   * Toggle sound on/off
   */
  toggleSound(enabled) {
    this.soundEnabled = enabled;
    this.savePreferences();
  }

  /**
   * Test notification (for settings page)
   */
  async testNotification() {
    this.playSound('notification');
    
    if (this.isEnabled()) {
      return this.sendPushNotification('🔔 Test Notification', {
        body: 'Push notifications are working!',
        tag: 'test',
        forceShow: true,
        timeout: 3000
      });
    }
    
    return null;
  }
}

// Singleton instance
const notificationService = new NotificationService();

export default notificationService;
